import { Link, useParams } from 'react-router-dom';
import { toDisplayError } from '../../lib/errors';
import { Alert } from '../../components/ui/Alert';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Spinner } from '../../components/ui/Spinner';
import { usePortalDossier, useDownloadPortalDocument } from './hooks';

/**
 * LOT B3 — détail d'un dossier client : informations essentielles + documents VALIDÉS (FINALISE) avec
 * téléchargement du PDF. Aucun document non validé n'est renvoyé par l'API, donc jamais affiché.
 */
export function PortalDossierPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const query = usePortalDossier(id);
  const download = useDownloadPortalDocument();

  if (query.isPending) return <Spinner label="Chargement du dossier…" />;
  if (query.isError) {
    return (
      <Alert variant="error" title="Dossier indisponible">
        {toDisplayError(query.error).message}
      </Alert>
    );
  }

  const dossier = query.data;

  return (
    <section className="mx-auto max-w-3xl space-y-4">
      <Link to="/portail" className="text-sm text-brand-700 hover:underline">
        ← Mes dossiers
      </Link>

      <Card>
        <CardHeader>
          <CardTitle>{dossier.numero}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          <div className="flex flex-wrap items-center gap-2">
            <Badge tone="info">{dossier.statut}</Badge>
            <span className="text-slate-500">{dossier.mandantNom}</span>
          </div>
          {dossier.objet ? <p className="text-slate-700">{dossier.objet}</p> : null}
        </CardContent>
      </Card>

      <div>
        <h2 className="mb-2 text-lg font-semibold text-slate-800">Documents validés</h2>
        {download.isError ? (
          <Alert variant="error" title="Téléchargement impossible" className="mb-2">
            {toDisplayError(download.error).message}
          </Alert>
        ) : null}
        {dossier.documents.length === 0 ? (
          <Card>
            <CardContent className="py-8 text-center text-sm text-slate-500">
              Aucun document validé n'est disponible pour ce dossier.
            </CardContent>
          </Card>
        ) : (
          <ul className="space-y-2">
            {dossier.documents.map((doc) => (
              <li
                key={doc.id}
                className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 bg-white p-4"
              >
                <div>
                  <p className="font-medium text-slate-800">{doc.displayLabel}</p>
                  <p className="text-xs text-slate-500">{doc.documentType}</p>
                </div>
                <Button
                  size="sm"
                  loading={download.isPending && download.variables === doc.id}
                  onClick={() => download.mutate(doc.id)}
                >
                  Télécharger le PDF
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
